import { useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { projectFirestore, projectStorage } from "../../firebase-config/config";
import "./VendorDetailsUploadDoc.scss";
import { useAuthContext } from "../../firebase-files/hooks/useAuthContext";

const MAX_FILE_SIZE = 4 * 1024 * 1024;

const VendorDetailsUploadDoc = ({
  selectedDocType,
  vendorOrganisationDoc,
  vendorProfileId,
  updateDocument,
  folderName,
  title,
  className = "",
  childIndex = 1,
}) => {
  const user = useAuthContext();
  const fileInputRefs = useRef({});
  const [isDocUploading, setIsDocUploading] = useState({});
  const [files, setFiles] = useState({});
  const [uploadDocInProgress, setUploadDocInProgress] = useState({});
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [completionStatus, setCompletionStatus] = useState({});
  const [isDragging, setIsDragging] = useState(false);

  const filePath = (file, docType) => {
    switch (folderName) {
      case "spouse-doc":
      case "father-doc":
      case "mother-doc":
        return `vendor-docs/${vendorProfileId}/${folderName}/${file.name}`;
      case "children-doc":
        return `vendor-docs/${vendorProfileId}/${folderName}/${childIndex}/${file.name}`;
      default:
        return `vendor-docs/${vendorProfileId}/${docType}/${file.name}`;
    }
  };

  const getDocKeys = (docType) => {
    const baseKey = docType.replace(/ /g, "").toLowerCase();
    return {
      urlKey: `${baseKey}Url`,
      typeKey: `${baseKey}Type`,
      updatedAtKey: `${baseKey}UpdatedAt`,
    };
  };

  useEffect(() => {
    if (!vendorProfileId) return;

    const docRef = projectFirestore
      .collection("organisations")
      .doc(vendorProfileId.id);

    const unsubscribe = docRef.onSnapshot((docSnap) => {
      if (docSnap.exists) {
        const userData = docSnap.data();
        setCompletionStatus((prev) => ({
          ...prev,
          [selectedDocType]: userData[getDocKeys(selectedDocType).urlKey]
            ? { status: "success", statusMsg: "Uploaded" }
            : { status: "pending", statusMsg: "Not Uploaded" },
        }));
      }
    });

    return () => unsubscribe(); // Cleanup listener
  }, [vendorProfileId, selectedDocType]);

  const allowedTypes = [
    "image/png",
    "image/jpeg",
    "image/jpg",
    "application/pdf",
  ];

  const handleDocFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      alert("File size exceeds the 10MB limit. Please upload a smaller file.");
      return;
    }
    if (!allowedTypes.includes(file.type)) {
      alert("Invalid file type. Only PNG, JPG, JPEG, and PDF are allowed.");
      return;
    }
    setFiles((prev) => {
      const updatedFiles = { ...prev, [selectedDocType]: file };
      return updatedFiles;
    });

    uploadDocument(selectedDocType, file);
  };
  const getFileType = (file) => {
    if (!file) {
      console.error("File is undefined");
      return;
    }
    const fileExtension = file.name.split(".").pop().toLowerCase();
    return fileExtension === "pdf" ? "pdf" : "image";
  };

  // upload document code with delete previous doc from storage automatically
  const uploadDocument = async (docType, file) => {
    try {
      setIsDocUploading((prev) => ({ ...prev, [docType]: true }));
      if (!file) {
        console.error("File is undefined", file);
        return;
      }
      console.log(file);
      const fileType = getFileType(file);
      // Check if there's an existing document and delete it
      const { urlKey, typeKey, updatedAtKey } = getDocKeys(docType);
      if (vendorOrganisationDoc && vendorOrganisationDoc[urlKey]) {
        const oldFileURL = vendorOrganisationDoc[urlKey];
        const oldFileRef = projectStorage.refFromURL(oldFileURL);

        try {
          await oldFileRef.delete();
          console.log(`Previous file for ${docType} deleted successfully.`);
        } catch (error) {
          console.error(`Error deleting previous file for ${docType}:`, error);
        }
      }
      setUploadDocInProgress((prev) => ({ ...prev, [docType]: 0 }));
      // Upload the new document
      const storageRef = projectStorage.ref(filePath(file, docType));
      const uploadTask = storageRef.put(file);
      uploadTask.on(
        "state_changed",
        (snapshot) => {
          const progress =
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadDocInProgress((prev) => ({ ...prev, [docType]: progress }));
        },
        (error) => {
          console.error(`Error uploading ${docType}:`, error);
          setCompletionStatus((prev) => ({
            ...prev,
            [docType]: { status: "error", statusMsg: "Upload Failed" },
          }));
          setIsDocUploading((prev) => ({ ...prev, [docType]: false }));
          setUploadDocInProgress((prev) => ({ ...prev, [docType]: 0 }));
        },
        async () => {
          const fileURL = await storageRef.getDownloadURL();
          if (folderName === "children-doc") {
            const userDocRef = projectFirestore
              .collection("users")
              .doc(vendorProfileId);
            const userDoc = await userDocRef.get();
            const userData = userDoc.data();
            const childrenArray = userData.dependentsData?.children || [];

            const updatedDocument = {
              ...childrenArray[childIndex - 1].document,
              [urlKey]: fileURL,
              [typeKey]: fileType,
              [updatedAtKey]: new Date().toISOString(),
            };
            childrenArray[childIndex - 1] = {
              ...childrenArray[childIndex - 1],
              document: updatedDocument,
            };

            await userDocRef.update({
              "dependentsData.children": childrenArray,
            });
          } else if (folderName === "spouse-doc") {
            await projectFirestore
              .collection("users")
              .doc(vendorProfileId)
              .update({
                [`dependentsData.spouse.document.${urlKey}`]: fileURL,
                [`dependentsData.spouse.document.${typeKey}`]: fileType,
                [`dependentsData.spouse.document.${updatedAtKey}`]: new Date(
                  Date.now()
                ).toISOString(),
              });
          } else if (folderName === "father-doc") {
            await projectFirestore
              .collection("users")
              .doc(vendorProfileId)
              .update({
                [`parentsDetails.father.document.${urlKey}`]: fileURL,
                [`parentsDetails.father.document.${typeKey}`]: fileType,
                [`parentsDetails.father.document.${updatedAtKey}`]: new Date(
                  Date.now()
                ).toISOString(),
              });
          } else if (folderName === "mother-doc") {
            await projectFirestore
              .collection("users")
              .doc(vendorProfileId)
              .update({
                [`parentsDetails.mother.document.${urlKey}`]: fileURL,
                [`parentsDetails.mother.document.${typeKey}`]: fileType,
                [`parentsDetails.mother.document.${updatedAtKey}`]: new Date(
                  Date.now()
                ).toISOString(),
              });
          } else {
            await updateDocument(vendorProfileId.id, {
              [urlKey]: fileURL,
              [typeKey]: fileType,
              [updatedAtKey]: new Date(Date.now()).toISOString(),
            });
          }
          setFiles((prev) => {
            const updatedFiles = { ...prev };
            delete updatedFiles[docType];
            return updatedFiles;
          });
          setCompletionStatus((prev) => ({
            ...prev,
            [docType]: {
              status: "success",
              statusMsg: "Uploaded",
            },
          }));
          setIsDocUploading((prev) => ({ ...prev, [docType]: false }));
        }
      );
    } catch (error) {
      console.error(`Error uploading ${docType}:`, error);
      setCompletionStatus((prev) => ({
        ...prev,
        [docType]: {
          status: "error",
          statusMsg: "Failed",
        },
      }));
      setIsDocUploading((prev) => ({ ...prev, [docType]: false }));
    }
  };

  // code for delete uploaded document from database and storage both
  const handleDeleteDocument = async (docType) => {
    try {
      setIsDocUploading((prev) => ({ ...prev, [docType]: true }));

      const { urlKey, typeKey, updatedAtKey } = getDocKeys(docType);

      if (vendorOrganisationDoc && vendorOrganisationDoc[urlKey]) {
        const oldFileURL = vendorOrganisationDoc[urlKey];
        const oldFileRef = projectStorage.refFromURL(oldFileURL);

        try {
          await oldFileRef.delete();
          console.log(`File for ${docType} deleted successfully.`);
        } catch (error) {
          console.error(
            `Error deleting file from storage for ${docType}:`,
            error
          );
        }
      }

      const userDocRef = projectFirestore
        .collection("users")
        .doc(vendorProfileId);
      const userDoc = await userDocRef.get();
      const userData = userDoc.data();

      if (folderName === "children-doc") {
        const childrenArray = userData.dependentsData?.children || [];

        if (childrenArray[childIndex - 1]?.document) {
          // Remove document data while keeping child structure intact
          childrenArray[childIndex - 1] = {
            ...childrenArray[childIndex - 1],
            document: null,
          };

          await userDocRef.update({
            "dependentsData.children": childrenArray,
          });
        }
      } else if (folderName === "spouse-doc") {
        await userDocRef.update({
          [`dependentsData.spouse.document.${urlKey}`]: null,
          [`dependentsData.spouse.document.${typeKey}`]: null,
          [`dependentsData.spouse.document.${updatedAtKey}`]: null,
        });
      } else if (folderName === "father-doc") {
        await userDocRef.update({
          [`parentsDetails.father.document.${urlKey}`]: null,
          [`parentsDetails.father.document.${typeKey}`]: null,
          [`parentsDetails.father.document.${updatedAtKey}`]: null,
        });
      } else if (folderName === "mother-doc") {
        await userDocRef.update({
          [`parentsDetails.mother.document.${urlKey}`]: null,
          [`parentsDetails.mother.document.${typeKey}`]: null,
          [`parentsDetails.mother.document.${updatedAtKey}`]: null,
        });
      } else {
        // ✅ Default delete logic for other document types
        await updateDocument(vendorProfileId.id, {
          [urlKey]: null,
          [typeKey]: null,
          [updatedAtKey]: null,
        });
      }

      // Update UI state after deletion
      setFiles((prev) => {
        const updatedFiles = { ...prev };
        delete updatedFiles[docType];
        return updatedFiles;
      });

      setCompletionStatus((prev) => ({
        ...prev,
        [docType]: { status: "deleted", statusMsg: "Deleted" },
      }));

      setIsDocUploading((prev) => ({ ...prev, [docType]: false }));
    } catch (error) {
      console.error(`Error deleting ${docType}:`, error);
      setCompletionStatus((prev) => ({
        ...prev,
        [docType]: { status: "error", statusMsg: "Delete Failed" },
      }));
      setIsDocUploading((prev) => ({ ...prev, [docType]: false }));
    }
  };

  // code for delete upload document with confirmation popup
  const [showConfirmModalForDeleteDoc, setShowConfirmModalForDeleteDoc] =
    useState(false);
  // const [modalDocType, setModalDocType] = useState("");
  const [isUploadedDocDeleting, setIsUploadedDocDeleting] = useState(false);

  const handleDeleteConfirm = async () => {
    try {
      setIsUploadedDocDeleting(true);
      await handleDeleteDocument(selectedDocType); // Call the delete function
      setIsUploadedDocDeleting(false);
      setShowConfirmModalForDeleteDoc(false); // Close modal after successful deletion
    } catch (error) {
      console.error(`Error deleting ${selectedDocType}:`, error);
      setIsUploadedDocDeleting(false);
    }
  };

  const handleDeleteClick = (docType) => {
    setShowConfirmModalForDeleteDoc(true);
  };
  // code for redering file with delete button
  const renderFilePreview = (docType) => {
    const { urlKey, typeKey } = getDocKeys(docType);

    return (
      <div className="image_container_inner">
        {vendorOrganisationDoc?.[typeKey] === "image" ? (
          <img
            src={vendorOrganisationDoc?.[urlKey]}
            alt={`${docType} preview`}
          />
        ) : (
          <iframe
            title="PDF Viewer"
            src={vendorOrganisationDoc?.[urlKey]}
            className="document-preview"
            style={{
              width: "100%",
              aspectRatio: "3/2",
            }}
          />
        )}
      </div>
    );
  };
  //code ended for staff documents

  return (
    <div className={`pd-document-card ${className}`}>
      <div className="document-file">
        <div className="document-file-data">
          <div className="document-file-icon">
            <span class="material-symbols-outlined">description</span>
          </div>
          <div className="document-file-name">
            <h3>{title}</h3>
            <span>
              {!vendorOrganisationDoc?.[
                getDocKeys(selectedDocType).updatedAtKey
              ]
                ? "Not Provided Yet"
                : `Updated: ${
                    vendorOrganisationDoc[
                      getDocKeys(selectedDocType).updatedAtKey
                    ].split("T")[0]
                  }`}
            </span>
          </div>
        </div>
        <div
          className={`uploaded-status ${completionStatus?.[selectedDocType]?.status}-uploaded`}
        >
          {completionStatus?.[selectedDocType]?.statusMsg}
        </div>
      </div>
      <div className="document-option">
        {completionStatus?.[selectedDocType]?.status === "success" ? (
          <>
            <div
              className="document-preview"
              onClick={() => {
                setPreviewModalOpen(true);
                // setSelectedDocType(docType);
              }}
            >
              <span className="material-symbols-outlined">visibility</span>
            </div>

            {user &&
              vendorOrganisationDoc &&
              vendorOrganisationDoc.isApproved !== true &&
              vendorOrganisationDoc.isActive && (
                <div
                  className="document-delete"
                  onClick={() => handleDeleteClick(selectedDocType)}
                >
                  <span className="material-symbols-outlined">delete</span>
                </div>
              )}
          </>
        ) : user &&
          vendorOrganisationDoc &&
          vendorOrganisationDoc.isApproved !== true &&
          vendorOrganisationDoc.isActive ? (
          <button
            className="document-upload"
            onClick={() => {
              setUploadModalOpen(true);
            }}
            disabled={isDocUploading[selectedDocType]}
          >
            {isDocUploading[selectedDocType] ? "Uploading..." : "Upload"}
          </button>
        ) : null}
      </div>
      {/* Uploading Modal */}
      <Modal
        show={uploadModalOpen}
        onHide={() => setUploadModalOpen(false)}
        centered
        size="lg"
        className="upload-modal"
      >
        <Modal.Header closeButton className="upload-modal-header">
          <div>
            <h3>Upload {selectedDocType}</h3>
            <p>Drop your images or pdf file here or click to browse</p>
          </div>
        </Modal.Header>
        <Modal.Body>
          {isDocUploading[selectedDocType] ||
          completionStatus[selectedDocType]?.status !== "pending" ? (
            <div className="upload-progress">
              <p>
                {completionStatus[selectedDocType]?.status === "success" ? (
                  <span class="material-symbols-outlined">check_circle</span>
                ) : completionStatus[selectedDocType]?.status === "error" ? (
                  <span class="material-symbols-outlined">warning</span>
                ) : null}
                {completionStatus[selectedDocType]?.status === "success"
                  ? "Uploaded"
                  : "Uploading"}
                {Math.round(uploadDocInProgress[selectedDocType])}%
              </p>
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{
                    width: `${Math.round(
                      uploadDocInProgress[selectedDocType]
                    )}%`,
                  }}
                />
              </div>
            </div>
          ) : (
            <div
              onClick={() => fileInputRefs.current[selectedDocType]?.click()}
              className="upload-modal-body"
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragging(true);
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                setIsDragging(false);
              }}
              onDrop={(e) => {
                e.preventDefault();
                setIsDragging(false);
                const file = e.dataTransfer.files[0];
                if (file) {
                  setFiles((prev) => ({ ...prev, [selectedDocType]: file }));
                  uploadDocument(selectedDocType, file);
                }
              }}
            >
              <div className="upload-icon">
                <span class="material-symbols-outlined">upload</span>
              </div>
              <div className="upload-info">
                <p className="upload-text-1">
                  <span className="text-primary">Click to upload </span>
                  or drag and drop
                </p>
                <p className="upload-text-2">PNG, JPG, JPEG, PDF upto 10MB</p>
              </div>
              <input
                type="file"
                name=""
                id=""
                ref={(el) => (fileInputRefs.current[selectedDocType] = el)}
                onChange={(e) => handleDocFileChange(e)}
                style={{ display: "none" }}
              />
            </div>
          )}
        </Modal.Body>
        {completionStatus[selectedDocType]?.status !== "pending" && (
          <Modal.Footer style={{ border: "none" }}>
            <button
              onClick={() => setUploadModalOpen(false)}
              className="upload-close-btn"
            >
              Close
            </button>
          </Modal.Footer>
        )}
      </Modal>
      {/* Preview Modal */}
      <Modal
        show={previewModalOpen}
        onHide={() => setPreviewModalOpen(false)}
        centered
        size="lg"
      >
        <Modal.Header
          closeButton
          // style={{ backgroundColor: "#000" }}
        >
          <Modal.Title>{selectedDocType}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{renderFilePreview(selectedDocType)}</Modal.Body>
      </Modal>
      {/* Delete Modal */}
      <Modal
        show={showConfirmModalForDeleteDoc}
        onHide={() => setShowConfirmModalForDeleteDoc(false)}
        centered
      >
        <Modal.Header
          className="justify-content-center"
          style={{
            paddingBottom: "0px",
            border: "none",
          }}
        >
          <h5>Confirm Deletion</h5>
        </Modal.Header>
        <Modal.Body
          className="text-center"
          style={{
            color: "#FA6262",
            fontSize: "20px",
            border: "none",
          }}
        >
          Are you sure you want to delete the uploaded {selectedDocType}?
        </Modal.Body>
        <Modal.Footer
          className="d-flex justify-content-between"
          style={{
            border: "none",
            gap: "15px",
          }}
        >
          <div
            className="cancel_btn"
            onClick={handleDeleteConfirm}
            disabled={isUploadedDocDeleting}
          >
            {isUploadedDocDeleting ? "Deleting..." : "Yes"}
          </div>
          <div
            className="done_btn"
            onClick={() => setShowConfirmModalForDeleteDoc(false)}
          >
            No
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default VendorDetailsUploadDoc;
